import { Formik, FormikProps } from 'formik';
import { Form, message } from 'antd';
import type { ReactNode } from 'react';
import { CustomType, NewTodoDetail } from './FormItem';
import { useContext } from 'react';
import { ToDoBundleContext } from './context';
import { createTodoInstance } from './api';
import { TypeSourceContext } from './TypeSourceContext';
import { officialTypeId } from 'views/Todo/hooks/useLabel';
import { useRefresh } from './useApi';
import { useSetAtom } from 'jotai';
import { todoStatus } from './state';
import { useNewTodoDrawerVisible, useSetNewTodoDrawerVisible } from './NewTodo';
import { useTranslate } from '@tolgee/react';
import { getTodoCache, TodoCache, useClearTodoCache } from './TodoCache';

type NewTodoFormProps = {
  children: (node: ReactNode, props: FormikProps<Todo.CreateTodoParams>) => ReactNode;
  visible?: boolean;
};

export const NewTodoForm = (props: NewTodoFormProps) => {
  const { children, visible } = props;
  const { storeId, externalBuyerId, assistantId } = useContext(ToDoBundleContext);
  const { taskId, typeSource } = useContext(TypeSourceContext);
  const { messageId } = useNewTodoDrawerVisible();
  const refresh = useRefresh();
  const { t } = useTranslate();
  const setVisible = useSetNewTodoDrawerVisible();
  const setTodoStatus = useSetAtom(todoStatus);
  const clearTodoCache = useClearTodoCache();

  return (
    <Formik<Todo.CreateTodoParams>
      initialValues={
        {
          description: [],
          typeName: '',
          ...(getTodoCache(storeId, externalBuyerId) || {}),
        } as unknown as Todo.CreateTodoParams
      }
      onSubmit={(values, formikHelpers) => {
        // 商家待办类型，补充type_name字段
        const typeName = (() => {
          if (!officialTypeId.includes(values.type) && values.type !== -998) {
            return typeSource.find((item) => item.type === values.type)?.typeLabel;
          }
          return values.typeName;
        })();

        return createTodoInstance({
          ...values,
          storeId: storeId,
          tradeBuyerId: externalBuyerId,
          taskId: taskId!,
          typeName: typeName!,
          relatedMsgId: messageId || '',
        })
          .then(() => {
            refresh(() => {
              if (assistantId === values.operator) {
                setTodoStatus('4');
              }
            });
            message.success(t('create_todo_success'));
            setVisible({ visible: false });
            formikHelpers.resetForm();
            clearTodoCache();
          })
          .catch(() => {
            message.error(t('phrase.保存失败，请稍后再试'));
          });
      }}
    >
      {(formProps) => {
        const node = (
          <Form layout="vertical">
            <div className="px-[12px] py-[16px]">
              <TodoCache
                key={getTodoCache(storeId, externalBuyerId)}
                storeId={storeId}
                externalBuyerId={externalBuyerId}
                values={formProps.values}
                visible={visible}
              />
              <CustomType />
              <NewTodoDetail />
            </div>
          </Form>
        );
        return children(node, formProps);
      }}
    </Formik>
  );
};
